import {
  useUpdateFeePeriodMutation,
  FeePeriodsByAccountDocument,
} from '@transactions/graphql';
import {fromSeconds} from 'common/date';
import * as React from 'react';
import {Button} from 'src/Button';
import {CardProductSelect} from 'src/CardProductSelect';
import DatePicker from 'src/DatePicker/DatePicker';
import {RawTransactionPicker} from 'src/inputs/RawTransactionPicker';
import {TextInput} from 'src/inputs/TextInput';
import {Modal} from 'src/Modal';
import {useModals} from 'src/Modals';
import type {FullFeePeriodFragment} from '@transactions/graphql';
import type {RawTransactionSelection} from 'src/inputs/RawTransactionPicker';
import type {SystemProps} from 'src/Modals';

export type EditFeePeriodModalUserProps = {
  feePeriod: FullFeePeriodFragment;
  onSuccess: () => void;
};

export const EditFeePeriodModal = ({
  onRequestClose,
  feePeriod,
  onSuccess,
}: EditFeePeriodModalUserProps & SystemProps) => {
  const formId = React.useId();
  const {deleteFeePeriodModal} = useModals();
  const [selectedTransaction, setSelectedTransaction] =
    React.useState<RawTransactionSelection | null>({
      _id: feePeriod.rawTransactionId,
      amount: feePeriod.amount,
      // Consumption state unknown from the fee period; treat as unconsumed
      remainingAmount: null,
      currency: 'USD',
      // Selection is display-only here; the account is not needed
      accountId: '',
      date: fromSeconds(feePeriod.startDate).toISODate() ?? '',
    });
  const [startDate, setStartDate] = React.useState(
    fromSeconds(feePeriod.startDate),
  );
  // Stored bounds are half-open; the picker shows the inclusive last day
  const [endDate, setEndDate] = React.useState(
    fromSeconds(feePeriod.endDate).minus({days: 1}),
  );
  const [payee, setPayee] = React.useState(feePeriod.payee ?? '');
  const [cardProduct, setCardProduct] = React.useState(
    feePeriod.cardProduct ?? '',
  );
  const [error, setError] = React.useState<string | null>(null);

  const [updateFeePeriod, {loading}] = useUpdateFeePeriodMutation();

  const handleTransactionSelected = (
    _id: string,
    tx?: RawTransactionSelection,
  ) => {
    setSelectedTransaction(tx ?? null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    try {
      if (!selectedTransaction)
        throw new Error('Fee transaction is required');

      if (!startDate.isValid) throw new Error('Invalid start date');
      if (!endDate.isValid) throw new Error('Invalid end date');

      await updateFeePeriod({
        variables: {
          id: feePeriod._id,
          rawTransactionId: selectedTransaction._id,
          startDate: Math.floor(startDate.toSeconds()),
          endDate: Math.floor(endDate.plus({days: 1}).toSeconds()),
          amount: Math.abs(selectedTransaction.amount),
          payee,
          cardProduct: cardProduct || null,
        },
        refetchQueries: [
          {
            query: FeePeriodsByAccountDocument,
            variables: {accountId: feePeriod.accountId},
          },
        ],
      });
      onSuccess();
      onRequestClose();
    } catch (e: unknown) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
      const err = e as Error;
      setError(err.message);
    }
  };

  const handleDelete = () => {
    onRequestClose();
    deleteFeePeriodModal.openModal({feePeriod, onDeleted: onSuccess});
  };

  const footer = (
    <>
      <Button variant="destructive" onClick={handleDelete}>
        Delete
      </Button>
      <div className="flex-1" />
      <Button variant="outline" onClick={onRequestClose}>
        Cancel
      </Button>
      <Button
        variant="primary"
        type="submit"
        form={formId}
        disabled={loading || !selectedTransaction}
      >
        {loading ? 'Saving...' : 'Save'}
      </Button>
    </>
  );

  return (
    <Modal
      onRequestClose={onRequestClose}
      header="Edit Fee Period"
      footer={footer}
    >
      {/* eslint-disable-next-line @typescript-eslint/no-misused-promises */}
      <form id={formId} onSubmit={handleSubmit} className="flex flex-col gap-4">
        {error && (
          <div className="bg-destructive/10 text-destructive rounded-md p-3 text-sm">
            {error}
          </div>
        )}

        <div className="flex flex-col gap-1">
          <label className="text-sm font-medium">Fee Transaction</label>
          <RawTransactionPicker
            accountId={feePeriod.accountId}
            value={selectedTransaction?._id ?? ''}
            onChange={handleTransactionSelected}
          />
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-sm font-medium">Card that year</label>
          <CardProductSelect
              value={cardProduct}
              onChange={setCardProduct}
              label={null}
              ariaLabel="Card that year"
            />
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-sm font-medium">Payee (Card Product Name)</label>
          <TextInput
            value={payee}
            onChange={setPayee}
            placeholder="e.g. Chase Sapphire Reserve"
          />
        </div>

        <div className="flex gap-2">
          <div className="flex flex-1 flex-col gap-1">
            <label className="text-sm font-medium">Start Date</label>
            <DatePicker selectedDate={startDate} onChange={setStartDate} />
          </div>
          <div className="flex flex-1 flex-col gap-1">
            <label className="text-sm font-medium">End Date</label>
            <DatePicker selectedDate={endDate} onChange={setEndDate} />
          </div>
        </div>
      </form>
    </Modal>
  );
};
